import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dropdown, DropdownProps } from 'src/modules/common/components/Dropdown';
import { FormError } from 'src/modules/form/types/FormError';

type Props<O, V> = Omit<DropdownProps<O>, 'selectedOption' |
'onChange' |
'invalid' |
'invalidText' |
'onBlur'> & {
  readonly field: FieldPath<V, FormError, O | null>;
  readonly readOnly?: boolean;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const DropdownField = <O, V>({
  field,
  renderError = renderErrorDefault,
  getOptionKey = String,
  options,
  readOnly,
  ...rest
}: Props<O, V>): React.ReactElement => {
  const { meta, value, error } = useFormField(field);

  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const selectedItem = useMemo(() => {
    if (value === null) {
      return null;
    }

    const key = getOptionKey(value);
    return options.find((option) => getOptionKey(option) === key) ?? null;
  }, [getOptionKey, options, value]);

  const handleChange = useCallback((option: O) => {
    field.form.change(pipe(
      field.form.currentState,
      field.value.set(option),
    ));
  }, [field]);

  const handleBlur = useCallback(() => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));
  }, [field]);

  return (
    <Dropdown<O>
      {...rest}
      id={`${field.form.name}.${field.path}`}
      name={field.path}
      options={options}
      selectedOption={selectedItem}
      onChange={handleChange}
      getOptionKey={getOptionKey}
      invalid={invalid}
      invalidText={error && invalid ? renderError(error) : null}
      onBlur={handleBlur}
      readOnly={readOnly}
    />
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error?.code}`} values={error?.context}/>
);
